import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { getBusinessReviews, getReviewStats, BusinessReview, ReviewStats } from '../../lib/api/reviews';
import { storage } from '../../utils/storage';

interface CredibilityTabProps {
  userId: string;
  isOwnProfile?: boolean;
  onWriteReview?: () => void;
}

const CredibilityTab: React.FC<CredibilityTabProps> = ({ userId, isOwnProfile, onWriteReview }) => {
  const { theme } = useTheme();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<BusinessReview[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);

  useEffect(() => {
    loadReviews();
  }, [userId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const token = await storage.getToken();
      if (!token) return;

      const [reviewsResponse, statsResponse] = await Promise.all([
        getBusinessReviews(token, userId),
        getReviewStats(token, userId),
      ]);

      if (reviewsResponse.success) {
        setReviews(reviewsResponse.reviews);
      }

      if (statsResponse.success) {
        setStats(statsResponse.stats);
      }
    } catch (error: any) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4.5) return '#10B981'; // Green
    if (rating >= 3.5) return '#22C55E'; // Light green
    if (rating >= 2.5) return '#FFB800'; // Yellow
    if (rating >= 1.5) return '#F59E0B'; // Orange
    return '#EF4444'; // Red
  };

  const renderRatingBar = (label: string, rating: number, icon: string) => {
    const percentage = (rating / 5) * 100;
    const color = getRatingColor(rating);

    return (
      <View style={styles.ratingBarContainer} key={label}>
        <View style={styles.ratingBarHeader}>
          <View style={styles.ratingBarLabel}>
            <Ionicons name={icon as any} size={18} color={theme.textSecondary} />
            <ThemedText style={[styles.ratingBarLabelText, { color: theme.text }]}>
              {label}
            </ThemedText>
          </View>
          <View style={styles.ratingBarValue}>
            <ThemedText style={[styles.ratingBarNumber, { color }]}>
              {rating.toFixed(1)}
            </ThemedText>
            <ThemedText style={[styles.ratingBarMax, { color: theme.textTertiary }]}>
              /5.0
            </ThemedText>
          </View>
        </View>
        <View style={[styles.ratingBarTrack, { backgroundColor: theme.border }]}>
          <View
            style={[
              styles.ratingBarFill,
              { backgroundColor: color, width: `${percentage}%` }
            ]}
          />
        </View>
      </View>
    );
  };

  const renderReviewCard = (review: BusinessReview) => {
    const avgRating = (
      review.overallRating +
      review.paymentsRating +
      review.serviceRating +
      review.qualityRating +
      review.workingRating +
      review.communicationRating +
      review.partialityRating +
      review.opportunitiesRating
    ) / 8;

    const handleProfilePress = () => {
      (navigation as any).push('Profile', { userId: review.reviewer.id });
    };

    return (
      <View
        key={review.id}
        style={[styles.reviewCard, { backgroundColor: theme.card, borderColor: theme.border }]}
      >
        <View style={styles.reviewHeader}>
          <TouchableOpacity 
            onPress={handleProfilePress}
            activeOpacity={0.7}
            style={styles.reviewerSection}
          >
            {review.reviewer.photo ? (
              <Image source={{ uri: review.reviewer.photo }} style={styles.reviewerAvatar} />
            ) : (
              <View style={[styles.reviewerAvatar, { backgroundColor: theme.backgroundSecondary }]}>
                <Ionicons name="person" size={24} color={theme.textSecondary} />
              </View>
            )}
            <View style={styles.reviewerInfo}>
              <ThemedText style={[styles.reviewerName, { color: theme.text }]}>
                {review.reviewer.name}
              </ThemedText>
              {review.reviewer.profession && (
                <ThemedText style={[styles.reviewerProfession, { color: theme.textSecondary }]}>
                  {review.reviewer.profession}
                </ThemedText>
              )}
            </View>
          </TouchableOpacity>
          <View style={styles.reviewRating}>
            <Ionicons name="star" size={16} color="#FFB800" />
            <ThemedText style={[styles.reviewRatingText, { color: theme.text }]}>
              {avgRating.toFixed(1)}
            </ThemedText>
          </View>
        </View>

        {review.reviewText && (
          <ThemedText style={[styles.reviewText, { color: theme.textSecondary }]}>
            {review.reviewText}
          </ThemedText>
        )}

        <View style={styles.reviewRatingsGrid}>
          {[
            { label: 'Overall', value: review.overallRating },
            { label: 'Payments', value: review.paymentsRating },
            { label: 'Service', value: review.serviceRating },
            { label: 'Quality', value: review.qualityRating },
            { label: 'Working', value: review.workingRating },
            { label: 'Communication', value: review.communicationRating },
            { label: 'Partiality', value: review.partialityRating },
            { label: 'Opportunities', value: review.opportunitiesRating },
          ].map((item) => (
            <View key={item.label} style={styles.reviewRatingItem}>
              <ThemedText style={[styles.reviewRatingItemLabel, { color: theme.textTertiary }]}>
                {item.label}
              </ThemedText>
              <View style={styles.reviewRatingItemValue}>
                {[...Array(5)].map((_, i) => (
                  <Ionicons
                    key={i}
                    name={i < item.value ? 'star' : 'star-outline'}
                    size={12}
                    color={i < item.value ? '#FFB800' : theme.border}
                  />
                ))}
              </View>
            </View>
          ))}
        </View>

        <ThemedText style={[styles.reviewDate, { color: theme.textTertiary }]}>
          {new Date(review.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </ThemedText>
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.accent} />
      </View>
    );
  }

  if (!stats || stats.totalReviews === 0) {
    return (
      <View style={styles.emptyContainer}>
        {/* Write Review Button - Show for others even when empty */}
        {!isOwnProfile && onWriteReview && (
          <TouchableOpacity
            style={[styles.writeReviewButton, { backgroundColor: theme.accent, marginBottom: 24 }]}
            onPress={onWriteReview}
            activeOpacity={0.7}
          >
            <Ionicons name="star" size={20} color="#FFFFFF" />
            <ThemedText style={styles.writeReviewButtonText}>Write a Review</ThemedText>
          </TouchableOpacity>
        )}
        
        <Ionicons name="star-outline" size={64} color={theme.textTertiary} />
        <ThemedText style={[styles.emptyTitle, { color: theme.text }]}>
          No Reviews Yet
        </ThemedText>
        <ThemedText style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
          {!isOwnProfile && onWriteReview ? 'Be the first to review!' : 'Reviews from other users will appear here'}
        </ThemedText>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 16 }]}
      showsVerticalScrollIndicator={false}
    >
      {/* Write Review Button - Show for others */}
      {!isOwnProfile && onWriteReview && (
        <TouchableOpacity
          style={[styles.writeReviewButton, { backgroundColor: theme.accent }]}
          onPress={onWriteReview}
          activeOpacity={0.7}
        >
          <Ionicons name="star" size={20} color="#FFFFFF" />
          <ThemedText style={styles.writeReviewButtonText}>Write a Review</ThemedText>
        </TouchableOpacity>
      )}

      {/* Rating Summary */}
      <View style={[styles.summaryCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        <View style={styles.summaryHeader}>
          <View style={styles.overallRating}>
            <ThemedText style={[styles.overallRatingNumber, { color: getRatingColor(stats.averageRatings.overall) }]}>
              {stats.averageRatings.overall.toFixed(1)}
            </ThemedText>
            <View style={styles.overallStars}>
              {[...Array(5)].map((_, i) => (
                <Ionicons
                  key={i}
                  name={i < Math.round(stats.averageRatings.overall) ? 'star' : 'star-outline'}
                  size={20}
                  color="#FFB800"
                />
              ))}
            </View>
            <ThemedText style={[styles.totalReviews, { color: theme.textSecondary }]}>
              Based on {stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'}
            </ThemedText>
          </View>
        </View>

        <View style={styles.ratingBarsContainer}>
          {renderRatingBar('Overall', stats.averageRatings.overall, 'star-outline')}
          {renderRatingBar('Payments', stats.averageRatings.payments, 'card-outline')}
          {renderRatingBar('Service', stats.averageRatings.service, 'hand-left-outline')}
          {renderRatingBar('Quality', stats.averageRatings.quality, 'ribbon-outline')}
          {renderRatingBar('Working', stats.averageRatings.working, 'briefcase-outline')}
          {renderRatingBar('Communication', stats.averageRatings.communication, 'chatbubbles-outline')}
          {renderRatingBar('Partiality', stats.averageRatings.partiality, 'scale-outline')}
          {renderRatingBar('Opportunities', stats.averageRatings.opportunities, 'rocket-outline')}
        </View>
      </View>

      {/* Individual Reviews */}
      <View style={styles.reviewsSection}>
        <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
          Reviews ({stats.totalReviews})
        </ThemedText>
        {reviews.map(renderReviewCard)}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginTop: 16,
  },
  emptySubtitle: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  writeReviewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 8,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  writeReviewButtonText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  summaryCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    marginBottom: 24,
  },
  summaryHeader: {
    marginBottom: 24,
  },
  overallRating: {
    alignItems: 'center',
  },
  overallRatingNumber: {
    fontSize: 48,
    fontWeight: '700',
  },
  overallStars: {
    flexDirection: 'row',
    gap: 4,
    marginTop: 8,
  },
  totalReviews: {
    fontSize: 14,
    marginTop: 8,
  },
  ratingBarsContainer: {
    gap: 16,
  },
  ratingBarContainer: {
    gap: 8,
  },
  ratingBarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  ratingBarLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  ratingBarLabelText: {
    fontSize: 14,
    fontWeight: '600',
  },
  ratingBarValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  ratingBarNumber: {
    fontSize: 16,
    fontWeight: '700',
  },
  ratingBarMax: {
    fontSize: 12,
    marginLeft: 2,
  },
  ratingBarTrack: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  ratingBarFill: {
    height: '100%',
    borderRadius: 4,
  },
  reviewsSection: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  reviewCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
  },
  reviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  reviewerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  reviewerAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 18,
    fontWeight: '700',
  },
  reviewerInfo: {
    flex: 1,
  },
  reviewerName: {
    fontSize: 16,
    fontWeight: '600',
  },
  reviewerProfession: {
    fontSize: 13,
    marginTop: 2,
  },
  reviewRating: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 184, 0, 0.1)',
  },
  reviewRatingText: {
    fontSize: 14,
    fontWeight: '700',
  },
  reviewText: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 16,
  },
  reviewRatingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 12,
  },
  reviewRatingItem: {
    width: '48%',
  },
  reviewRatingItemLabel: {
    fontSize: 12,
    marginBottom: 4,
  },
  reviewRatingItemValue: {
    flexDirection: 'row',
    gap: 2,
  },
  reviewDate: {
    fontSize: 12,
    marginTop: 8,
  },
});

export default CredibilityTab;
